/**
 * SPDX-FileCopyrightText: 2026 Conduction B.V.
 * SPDX-License-Identifier: EUPL-1.2
 *
 * widgetIcons — curated Material Design Icon registry shared by the catalog
 * dashboard widgets (menu, links, tile) ported from launchpad as part of the
 * cn-widget-library Wave 1 migration.
 *
 * The `icon` field on a widget item/tile may hold one of three values:
 *
 *   - `null` / `''` — render the {@link DEFAULT_ICON}
 *   - A registry key (e.g. `'ViewDashboard'`) — looked up in {@link DASHBOARD_ICONS}
 *   - A URL (starts with `/` or `http`) — render as an `<img>`; use
 *     {@link isCustomIconUrl} as the discriminator.
 *
 * Each icon is imported individually (no wildcard / barrel) so the production
 * bundle stays tree-shake-friendly.
 *
 * @spec openspec/changes/cn-widget-library/specs/cn-widget-library/spec.md
 */

import AccountIcon from 'vue-material-design-icons/Account.vue'
import AccountBoxOutlineIcon from 'vue-material-design-icons/AccountBoxOutline.vue'
import AccountGroupIcon from 'vue-material-design-icons/AccountGroup.vue'
import AlertCircleOutlineIcon from 'vue-material-design-icons/AlertCircleOutline.vue'
import BellIcon from 'vue-material-design-icons/Bell.vue'
import BookOpenVariantIcon from 'vue-material-design-icons/BookOpenVariant.vue'
import BriefcaseIcon from 'vue-material-design-icons/Briefcase.vue'
import CalendarIcon from 'vue-material-design-icons/Calendar.vue'
import CashIcon from 'vue-material-design-icons/Cash.vue'
import CashMultipleIcon from 'vue-material-design-icons/CashMultiple.vue'
import ChartBarIcon from 'vue-material-design-icons/ChartBar.vue'
import ChartLineIcon from 'vue-material-design-icons/ChartLine.vue'
import CheckCircleOutlineIcon from 'vue-material-design-icons/CheckCircleOutline.vue'
import ClipboardCheckOutlineIcon from 'vue-material-design-icons/ClipboardCheckOutline.vue'
import ClipboardListIcon from 'vue-material-design-icons/ClipboardListOutline.vue'
import CogIcon from 'vue-material-design-icons/Cog.vue'
import CurrencyEurIcon from 'vue-material-design-icons/CurrencyEur.vue'
import DatabaseOutlineIcon from 'vue-material-design-icons/DatabaseOutline.vue'
import EarthIcon from 'vue-material-design-icons/Earth.vue'
// ADR-062 fleet expansion (2026-07-08): detail-page widget headers need a
// broader domain vocabulary (cases, contacts, comms, logistics, ops). One
// shared list — hydra gate-32's ICON_REGISTRY mirrors it; keep them in sync.
import EmailIcon from 'vue-material-design-icons/Email.vue'
import FileDocumentIcon from 'vue-material-design-icons/FileDocument.vue'
import FileSignIcon from 'vue-material-design-icons/FileSign.vue'
import FilterVariantIcon from 'vue-material-design-icons/FilterVariant.vue'
import FolderOutlineIcon from 'vue-material-design-icons/FolderOutline.vue'
import GaugeIcon from 'vue-material-design-icons/Gauge.vue'
import GavelIcon from 'vue-material-design-icons/Gavel.vue'
import HandshakeOutlineIcon from 'vue-material-design-icons/HandshakeOutline.vue'
import HeartIcon from 'vue-material-design-icons/Heart.vue'
import HistoryIcon from 'vue-material-design-icons/History.vue'
import HomeIcon from 'vue-material-design-icons/Home.vue'
import LightbulbIcon from 'vue-material-design-icons/Lightbulb.vue'
import LinkVariantIcon from 'vue-material-design-icons/LinkVariant.vue'
import MapMarkerIcon from 'vue-material-design-icons/MapMarker.vue'
import MapIcon from 'vue-material-design-icons/MapOutline.vue'
import MessageTextOutlineIcon from 'vue-material-design-icons/MessageTextOutline.vue'
import NoteTextOutlineIcon from 'vue-material-design-icons/NoteTextOutline.vue'
import OfficeBuildingIcon from 'vue-material-design-icons/OfficeBuilding.vue'
import PackageIcon from 'vue-material-design-icons/Package.vue'
import PercentIcon from 'vue-material-design-icons/Percent.vue'
import PhoneIcon from 'vue-material-design-icons/Phone.vue'
// Agents. `Agent.icon` documents "RobotOutline" as its own example and is
// picked with CnIconPicker, which offers exactly this registry — so its absence
// meant the schema's documented example resolved to the default dashboard glyph.
import RobotOutlineIcon from 'vue-material-design-icons/RobotOutline.vue'
import RocketLaunchIcon from 'vue-material-design-icons/RocketLaunch.vue'
import ScaleBalanceIcon from 'vue-material-design-icons/ScaleBalance.vue'
import SchoolIcon from 'vue-material-design-icons/School.vue'
import ShieldCheckOutlineIcon from 'vue-material-design-icons/ShieldCheckOutline.vue'
import SitemapIcon from 'vue-material-design-icons/Sitemap.vue'
import SourceBranchIcon from 'vue-material-design-icons/SourceBranch.vue'
import StarIcon from 'vue-material-design-icons/Star.vue'
import TableColumnIcon from 'vue-material-design-icons/TableColumn.vue'
import TagOutlineIcon from 'vue-material-design-icons/TagOutline.vue'
import TimelineIcon from 'vue-material-design-icons/Timeline.vue'
import TrendingDownIcon from 'vue-material-design-icons/TrendingDown.vue'
import TrendingUpIcon from 'vue-material-design-icons/TrendingUp.vue'
import TrophyIcon from 'vue-material-design-icons/Trophy.vue'
import TruckIcon from 'vue-material-design-icons/Truck.vue'
import ViewDashboardIcon from 'vue-material-design-icons/ViewDashboard.vue'
import WebIcon from 'vue-material-design-icons/Web.vue'

/**
 * Map of icon registry name → Vue component reference. Iteration order is the
 * order options appear in pickers.
 *
 * @type {Record<string, object>}
 */
export const DASHBOARD_ICONS = Object.freeze({
	ViewDashboard: ViewDashboardIcon,
	Home: HomeIcon,
	ChartBar: ChartBarIcon,
	Cog: CogIcon,
	AccountGroup: AccountGroupIcon,
	Calendar: CalendarIcon,
	FileDocument: FileDocumentIcon,
	Bell: BellIcon,
	Star: StarIcon,
	Heart: HeartIcon,
	BookOpenVariant: BookOpenVariantIcon,
	Lightbulb: LightbulbIcon,
	RocketLaunch: RocketLaunchIcon,
	Earth: EarthIcon,
	Briefcase: BriefcaseIcon,
	Cash: CashIcon,
	CashMultiple: CashMultipleIcon,
	CurrencyEur: CurrencyEurIcon,
	Trophy: TrophyIcon,
	TrendingUp: TrendingUpIcon,
	TrendingDown: TrendingDownIcon,
	Gauge: GaugeIcon,
	ChartLine: ChartLineIcon,
	ScaleBalance: ScaleBalanceIcon,
	FilterVariant: FilterVariantIcon,
	Percent: PercentIcon,
	Account: AccountIcon,
	RobotOutline: RobotOutlineIcon,
	ClipboardList: ClipboardListIcon,
	Map: MapIcon,
	TableColumn: TableColumnIcon,
	FolderOutline: FolderOutlineIcon,
	AlertCircleOutline: AlertCircleOutlineIcon,
	CheckCircleOutline: CheckCircleOutlineIcon,
	ClipboardCheckOutline: ClipboardCheckOutlineIcon,
	// ADR-062 fleet expansion — mirror any change into hydra gate-32.
	Email: EmailIcon,
	Phone: PhoneIcon,
	School: SchoolIcon,
	MapMarker: MapMarkerIcon,
	Gavel: GavelIcon,
	FileSign: FileSignIcon,
	Truck: TruckIcon,
	Package: PackageIcon,
	HandshakeOutline: HandshakeOutlineIcon,
	OfficeBuilding: OfficeBuildingIcon,
	Web: WebIcon,
	ShieldCheckOutline: ShieldCheckOutlineIcon,
	History: HistoryIcon,
	AccountBoxOutline: AccountBoxOutlineIcon,
	NoteTextOutline: NoteTextOutlineIcon,
	MessageTextOutline: MessageTextOutlineIcon,
	Sitemap: SitemapIcon,
	SourceBranch: SourceBranchIcon,
	Timeline: TimelineIcon,
	LinkVariant: LinkVariantIcon,
	DatabaseOutline: DatabaseOutlineIcon,
	TagOutline: TagOutlineIcon,
})

/**
 * The fallback icon name used when no icon is set or the requested name is not
 * in the registry.
 *
 * @type {string}
 */
export const DEFAULT_ICON = 'ViewDashboard'

/**
 * Discriminator for the `icon` field — true when the value should be rendered
 * as an `<img>` (a URL) rather than looked up in the registry.
 *
 * @param {string|null|undefined} name the value from a widget icon field.
 * @return {boolean} true when `name` is a non-empty string starting with `/`,
 *   `http`, or `data:` (an inline data-URI, e.g. the bundled NL Design icons).
 */
export function isCustomIconUrl(name) {
	if (typeof name !== 'string' || name.length === 0) {
		return false
	}
	return name.startsWith('/') || name.startsWith('http') || name.startsWith('data:')
}

/**
 * Resolve an icon name to a Vue component reference. Returns `null` when the
 * name is a URL (the caller must render via `<img>`); registry names, null,
 * undefined, empty, and unknown names all resolve to the {@link DEFAULT_ICON}
 * component. Never throws on non-URL inputs.
 *
 * @param {string|null|undefined} name icon registry key, URL, or null/empty.
 * @return {object|null} a Vue component for `<component :is>`, or `null` for a URL.
 */
export function getIconComponent(name) {
	if (isCustomIconUrl(name)) {
		return null
	}
	if (typeof name !== 'string' || name.length === 0) {
		return DASHBOARD_ICONS[DEFAULT_ICON]
	}
	return DASHBOARD_ICONS[name] || DASHBOARD_ICONS[DEFAULT_ICON]
}

/**
 * Whether a name resolves to a REAL icon in this registry — i.e. `getIconComponent`
 * would return that icon rather than silently falling back to {@link DEFAULT_ICON}.
 *
 * Callers that decide *whether to render an icon at all* need this: asking
 * `getIconComponent` is useless for that, because it answers "ViewDashboard" for
 * every unknown name, so an unrecognised value would render a wrong (but plausible)
 * icon instead of none. `icon-*` values are Nextcloud CSS-class icons, rendered
 * through a different path, and are never registry names.
 *
 * @param {unknown} name The icon value from a menu/widget icon field.
 * @return {boolean} True when the registry has a component for this exact name.
 */
export function hasRegistryIcon(name) {
	if (typeof name !== 'string' || name.length === 0) {
		return false
	}
	if (name.startsWith('icon-')) {
		return false
	}
	return Object.hasOwn(DASHBOARD_ICONS, name)
}
